const HERO_IMAGE =
  'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&w=1600&q=80';

const wikiPhoto = (file) =>
  `https://commons.wikimedia.org/wiki/Special:FilePath/${encodeURIComponent(file)}?width=1600`;

const googleMaps = (query) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

const osmEmbed = (lat, lon, delta = 0.016) => {
  const minLon = lon - delta;
  const minLat = lat - delta * 0.65;
  const maxLon = lon + delta;
  const maxLat = lat + delta * 0.65;
  return `https://www.openstreetmap.org/export/embed.html?bbox=${minLon},${minLat},${maxLon},${maxLat}&layer=mapnik&marker=${lat},${lon}`;
};

const PHOTOS = {
  highbanksBridge: wikiPhoto('Arch bridge in Highbanks Metro Park, October 2024.jpg'),
  highbanksTrees: wikiPhoto('Trees in Highbanks Park.JPG'),
  ashCave: wikiPhoto('Ash Cave @ Hocking Hills State Park - panoramio.jpg'),
  oldMansCave: wikiPhoto("Old Man's Cave @ Hocking Hills State Park, Ohio - panoramio.jpg"),
  cedarFalls: wikiPhoto('HockingHillsCedarFalls.jpg'),
  conklesHollow: wikiPhoto('Conkles Hollow.jpg'),
  brandywine: wikiPhoto('Brandywine Falls (29999191633).jpg'),
  cemeteryRidge: wikiPhoto('Cemetery Ridge Trail head (29278691062).jpg'),
  clearCreek: wikiPhoto('Clear Creek Metro Park (24765352707).jpg'),
};

const MAPS = {
  highbanks: {
    query: 'Highbanks Metro Park, 9466 Columbus Pike, Lewis Center, OH 43035',
    lat: 40.1467,
    lon: -83.0275,
  },
  shaleHollow: {
    query: 'Shale Hollow Preserve, 7799 Africa Rd, Lewis Center, OH 43035',
    lat: 40.2044,
    lon: -83.0006,
  },
  ashCave: {
    query: 'Ash Cave, Hocking Hills State Park, South Bloomingville, OH',
    lat: 39.3958,
    lon: -82.5458,
  },
  hockingVisitor: {
    query: 'Hocking Hills Visitor Center, 19852 State Route 664 S, Logan, OH 43138',
    lat: 39.4345,
    lon: -82.5415,
  },
  conklesHollow: {
    query: "Conkle's Hollow State Nature Preserve, Rockbridge, OH",
    lat: 39.4507,
    lon: -82.5748,
  },
  brandywine: {
    query: 'Brandywine Falls, 8176 Brandywine Rd, Sagamore Hills, OH 44067',
    lat: 41.27686,
    lon: -81.53848,
  },
  clearCreek: {
    query: 'Clear Creek Metro Park Creekside Meadows, 185 Clear Creek Rd, Rockbridge, OH 43149',
    lat: 39.5915,
    lon: -82.638,
  },
};

/**
 * Featured guide: beginner-friendly hiking trails across Ohio,
 * with a Central Ohio first-hike focus (Powell, Dublin, Lewis Center, Delaware).
 */
export const OHIO_BEGINNER_HIKING_POST = {
  id: 'featured-ohio-beginner-hiking-guide',
  slug: 'ohio-beginner-hiking-guide',
  title: 'Ohio Beginner Hiking Guide: Easy First Trails Across the State',
  excerpt:
    'Short, well-marked first hikes from Columbus metro parks to Hocking Hills and Cuyahoga Valley—what to pack, which trailheads to start at, and how to build up from a 30-minute walk to a real gorge day.',
  publishedAt: '2026-09-06T12:00:00.000Z',
  coverImageUrl: HERO_IMAGE,
  authorName: 'Daily Sports Life',
  clubName: 'Ohio Trail Guide',
  blogCategory: 'club-news',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Guide overview',
      items: [
        { value: '20+', label: 'Beginner-friendly trails' },
        { value: '0.5–3 mi', label: 'Typical first-hike distance' },
        { value: 'Free parks', label: 'Ohio state parks have no gate fee' },
        { value: 'Statewide', label: 'Metro parks + weekend day trips' },
      ],
    },
    {
      type: 'panel',
      title: 'How to use this guide',
      paragraphs: [
        '“Beginner hiking” here means well-marked trails you can finish in 30–90 minutes, with clear parking, modest elevation, and a payoff (overlook, waterfall, boardwalk, or creek) so the outing feels like a hike—not just a sidewalk loop.',
        'Start close to home. Central Ohio families in Powell, Dublin, Lewis Center, Polaris, and Delaware can practice on metro and preservation-park trails before driving to Hocking Hills or Cuyahoga Valley.',
        'Ohio state parks do not charge a gate fee. Metro parks and national park sites are also typically free; a few gardens and arboretums charge parking or admission. Distances below are round-trip unless noted. Always confirm trail conditions after heavy rain—Ohio clay and gorge stairs get slick.',
      ],
      chips: ['Beginner', 'Easy & well-marked', 'Ohio 2026', 'Columbus + statewide'],
    },
    {
      type: 'panel',
      title: 'What to pack (you do not need expedition gear)',
      list: [
        'Closed-toe shoes with tread—trail runners or hiking shoes beat sandals on roots, shale, and wet wood.',
        'Water for everyone; a small snack if you will be out more than an hour.',
        'Phone with an offline map screenshot; cell service drops in Hocking Hills and some gorge parks.',
        'Bug spray (woods and marsh, especially late spring through September) and sun protection for exposed overlooks.',
        'Light layer—Ohio mornings can be cool even when the parking lot feels warm.',
        'Optional: trekking poles if knees or balance are a concern on stairs (Old Man’s Cave, Brandywine, Cedar Falls).',
      ],
      callout:
        'This is a recreation guide, not medical advice. If you are new to walking for exercise, start with the shortest paved options and build up. Stay on marked trails and keep a respectful distance from cliff edges.',
    },
    {
      type: 'table',
      title: 'Central Ohio — first hikes near Powell, Dublin, Lewis Center & Delaware',
      intro:
        'Do these before a weekend gorge trip. They teach trail etiquette, modest hills, and “turn around when you are tired” without a two-hour drive.',
      headers: ['Trail', 'Trailhead / address', 'Distance & grade', 'Why it is a good first hike'],
      rows: [
        [
          'Highbanks Overlook Trail',
          '9466 Columbus Pike (US 23 N), Lewis Center, OH 43035',
          '~0.5 mi · Easy',
          'Short walk to 100-ft Olentangy shale bluffs. Pair with the nature center and natural play area. Best “wow” hike within 15 minutes of Powell.',
        ],
        [
          'Highbanks Big Meadows Path',
          'Same Highbanks entrance',
          'Paved loop · Easy / stroller',
          'Fully paved multi-use path—ideal if you want a first outing with a stroller, wagon, or anyone who prefers even footing.',
        ],
        [
          'Shale Hollow Preserve',
          '7799 Africa Rd, Lewis Center, OH 43035',
          '~1–2.5 mi of linked trails · Easy–moderate',
          'Delaware County ravine with boardwalks, creek views, and a compact trail system. Closer to Polaris than most gorge parks; roots and some stairs—still beginner-friendly if you take it slow.',
        ],
        [
          'Battelle Darby Creek — bison + greenway',
          '1775 Darby Creek Dr, Galloway, OH 43119 (nature center / bison pastures)',
          'Flat gravel & boardwalk · Easy',
          'Watch the bison herd from the greenway, then add a short prairie or creek-side segment. Almost no climbing—great confidence builder.',
        ],
        [
          'Blendon Woods — Brookside / nature center loops',
          '4265 E Dublin Granville Rd, Westerville, OH 43081',
          '~1–2 mi · Easy',
          'Woodland creek valley, nature center, and short gorge-side paths. A calmer alternative to Highbanks on busy weekends.',
        ],
        [
          'Inniswood Metro Gardens',
          '940 S Hempstead Rd, Westerville, OH 43081',
          'Garden & woodland paths · Very easy',
          'Shady, well-maintained paths through gardens and woods. Use this as hike #1 if you want zero navigation stress.',
        ],
        [
          'Glacier Ridge — Ironweed / Marsh Hawk',
          '9801 Hyland Croy Rd, Plain City, OH 43064',
          'Paved loops · Easy',
          'Dublin / Plain City paved trails through prairie and wetland. Not a dirt hike—perfect for a first family walk on even surfaces.',
        ],
        [
          'Alum Creek shoreline (hiking, not MTB)',
          'Alum Creek State Park beach / day-use lots, Delaware / Lewis Center',
          'Easy lakeside walks · Easy',
          'Stay on hiking and paved paths—do not mix with COMBO mountain-bike loops. Combine a short shoreline walk with a beach or picnic stop. Full beginner-to-moderate plan: [Best Alum Creek hike spots](/blog/alum-creek-best-hike-spots-beginner-moderate).',
        ],
        [
          'Sharon Woods lake loops',
          '11450 Cleveland Ave, Westerville, OH 43081',
          '~1–2 mi · Easy',
          'Lake views, playgrounds, and gentle paths—good when you want a hike that still feels like a park day.',
        ],
      ],
    },
    {
      type: 'imageGallery',
      title: 'Central Ohio trail photos',
      intro:
        'Highbanks Metro Park in Lewis Center — the closest “real hike” feel for Powell, Polaris, and Delaware families. Photos: Wikimedia Commons.',
      images: [
        {
          url: PHOTOS.highbanksBridge,
          alt: 'Wooden arch footbridge over a creek at Highbanks Metro Park',
          caption: 'Creek footbridge on the Highbanks trail system, Lewis Center.',
          mapUrl: googleMaps(MAPS.highbanks.query),
        },
        {
          url: PHOTOS.highbanksTrees,
          alt: 'Wooded trail at Highbanks Metro Park',
          caption: 'Forest cover near the Overlook and Dripping Rock area.',
          mapUrl: googleMaps(MAPS.highbanks.query),
        },
      ],
    },
    {
      type: 'table',
      title: 'Southeast Ohio — Hocking Hills beginner routes (skip the rim trails at first)',
      intro:
        'Hocking Hills is Ohio’s signature hiking destination, but not every trail there is beginner-friendly. Start on paved gorge floors. Save rim trails, Cantwell Cliffs, and the full Grandma Gatewood connector until you are comfortable on stairs and uneven rock.',
      headers: ['Trail', 'Trailhead', 'Distance & grade', 'Notes'],
      rows: [
        [
          'Ash Cave Gorge Trail',
          'Ash Cave parking, Hocking Hills State Park (near South Bloomingville)',
          '~0.5 mi round trip · Easy, paved, ADA',
          'The best first Hocking Hills hike: a wide paved path to a huge recess cave and seasonal waterfall. Stroller- and wheelchair-friendly.',
        ],
        [
          'Conkle’s Hollow Gorge Trail',
          'Conkle’s Hollow State Nature Preserve (separate from the main park gorge)',
          '~1 mi round trip · Easy, paved, ADA',
          'Towering blackhand sandstone walls from a flat gorge floor. Do not take the Rim Trail as a beginner—it is narrow, clifftop, and not for first-timers or young kids.',
        ],
        [
          'Cedar Falls (lower approach)',
          'Cedar Falls parking area, Hocking Hills State Park',
          '~0.5–1 mi with stairs · Easy–moderate',
          'Short walk to one of Ohio’s prettiest waterfalls. Expect stairs and wet rock; turn around at the viewing area if that is enough.',
        ],
        [
          'Old Man’s Cave — visitor center to Upper Falls',
          'Hocking Hills Visitor Center, 19852 State Route 664 S, Logan, OH 43138',
          '~1 mi with stairs · Moderate-easy',
          'Ohio’s most famous gorge—and the busiest. Do the upper section first; skip the full gorge + A-frame-to-Ash-Cave marathon until later. Recessed cave viewing involves stairs and crowds.',
        ],
        [
          'Lake Hope State Park loops',
          '27331 State Route 278, McArthur, OH 45651',
          'Easy forest & lake trails · Easy',
          'Quieter than Hocking Hills. Hemlock woods and lakeside paths without cliff exposure—excellent second-weekend hike after Ash Cave.',
        ],
      ],
    },
    {
      type: 'imageGallery',
      title: 'Hocking Hills beginner trail photos',
      intro:
        'Start on paved gorge floors (Ash Cave, Conkle’s Hollow gorge). Old Man’s Cave and Cedar Falls add stairs. Photos: Wikimedia Commons.',
      images: [
        {
          url: PHOTOS.ashCave,
          alt: 'Ash Cave recess cave and seasonal waterfall, Hocking Hills State Park',
          caption: 'Ash Cave — 0.5-mile paved path to Ohio’s largest recess cave.',
          mapUrl: googleMaps(MAPS.ashCave.query),
        },
        {
          url: PHOTOS.conklesHollow,
          alt: 'Sandstone cliffs at Conkle’s Hollow State Nature Preserve',
          caption: 'Conkle’s Hollow cliffs — hike the paved gorge floor, not the rim, as a beginner.',
          mapUrl: googleMaps(MAPS.conklesHollow.query),
        },
        {
          url: PHOTOS.oldMansCave,
          alt: "Old Man's Cave gorge at Hocking Hills State Park",
          caption: 'Old Man’s Cave gorge near the visitor center (stairs; busiest trail).',
          mapUrl: googleMaps(MAPS.hockingVisitor.query),
        },
        {
          url: PHOTOS.cedarFalls,
          alt: 'Cedar Falls waterfall in Hocking Hills State Park',
          caption: 'Cedar Falls — short walk from parking with stairs and wet rock.',
          mapUrl: googleMaps('Cedar Falls parking, Hocking Hills State Park, Logan, OH'),
        },
      ],
    },
    {
      type: 'table',
      title: 'Northeast Ohio — Cuyahoga Valley & Lake Erie (flat to short-stairs)',
      intro:
        'Cuyahoga Valley National Park is made for beginners: a flat canal towpath, boardwalks, and a few short waterfall walks. You can hike here without ever scrambling on rock.',
      headers: ['Trail', 'Trailhead', 'Distance & grade', 'Notes'],
      rows: [
        [
          'Ohio & Erie Canal Towpath',
          'Boston Store, Peninsula, or Hunt Farm (CVNP)',
          'As far as you want · Easy, flat',
          'Crushed limestone / paved canal path. Walk 1–2 miles out and back for a true beginner “hike” with river and lock scenery. Bikes share the path—stay right.',
        ],
        [
          'Beaver Marsh boardwalk',
          'Ira Trailhead, 3801 Riverview Rd, Peninsula, OH 44264',
          '~0.5–1 mi · Very easy',
          'Boardwalk over wetlands with herons, turtles, and beaver lodges. Almost no elevation. Pair with a short Towpath segment.',
        ],
        [
          'Brandywine Falls boardwalk',
          'Brandywine Falls parking, 8176 Brandywine Rd, Sagamore Hills, OH 44067',
          'Short boardwalk + stairs · Easy–moderate',
          'Ohio’s most photographed waterfall in the national park. The upper platform is a short walk; the lower stairs add effort. Not a long hike—still a great first gorge view.',
        ],
        [
          'Headlands Beach & dunes',
          'Headlands Beach State Park, Mentor, OH',
          'Beach / dune walks · Easy',
          'Sand instead of roots. Combine a shoreline walk with Headlands Dunes State Nature Preserve. Best on cooler mornings; summer afternoons get crowded.',
        ],
        [
          'The Ledges (next step, not hike #1)',
          'Happy Days Lodge / Ledges Trailhead, CVNP',
          '~2.2 mi loop · Moderate',
          'Rock formations and a sunset overlook—save this until you have done Towpath + Brandywine. Uneven sandstone and some exposure.',
        ],
      ],
    },
    {
      type: 'imageGallery',
      title: 'Cuyahoga Valley — Brandywine Falls',
      intro:
        'The national park’s signature waterfall. Upper boardwalk is a short walk; lower stairs add effort. Photo: Wikimedia Commons (Erik Drost).',
      images: [
        {
          url: PHOTOS.brandywine,
          alt: 'Brandywine Falls in Cuyahoga Valley National Park',
          caption: 'Brandywine Falls from the viewing area — 8176 Brandywine Rd, Sagamore Hills.',
          mapUrl: googleMaps(MAPS.brandywine.query),
        },
      ],
    },
    {
      type: 'table',
      title: 'Southwest Ohio — gorges near Yellow Springs & Miami Valley',
      headers: ['Trail', 'Trailhead', 'Distance & grade', 'Notes'],
      rows: [
        [
          'Glen Helen Nature Preserve',
          '405 Corry St, Yellow Springs, OH 45387',
          'Easy creekside loops · Easy',
          'Old-growth feel, Raptor Center, and well-used paths minutes from town. A gentle half-day before you try Clifton Gorge.',
        ],
        [
          'Clifton Gorge — North / easier gorge paths',
          'Clifton Gorge State Nature Preserve, near Yellow Springs',
          'Short gorge walks · Easy–moderate',
          'Limestone gorge on the Little Miami. Stay on marked trails and well back from edges. Not a place to let kids run ahead.',
        ],
        [
          'Hueston Woods — Big Woods Trail',
          'Hueston Woods State Park, College Corner, OH',
          '~1–2 mi · Easy',
          'Old-growth beech-maple forest on a classic southwest Ohio loop. Pair with the lake beach or nature center.',
        ],
        [
          'Caesar Creek shoreline & fossil area',
          'Caesar Creek State Park, Waynesville / Warren County',
          'Easy lakeside · Easy',
          'Reservoir views and (with a free fossil permit) a unique add-on. Hiking itself is gentle; the fossil beach is the main draw for many first-timers.',
        ],
      ],
    },
    {
      type: 'table',
      title: 'Northwest Ohio — marshes, savanna, and Lake Erie',
      headers: ['Trail', 'Trailhead', 'Distance & grade', 'Notes'],
      rows: [
        [
          'Maumee Bay boardwalk',
          'Maumee Bay State Park, Oregon, OH (near Toledo)',
          'Boardwalk · Very easy',
          'Marsh boardwalk plus optional beach time. Flat, scenic, and beginner-proof—good if hills are a concern.',
        ],
        [
          'Oak Openings Preserve — easy prairie loops',
          'Oak Openings Preserve Metropark, Swanton, OH',
          'Easy sandy loops · Easy',
          'Rare oak savanna and dunes. Choose short, marked loops; some sand is slower walking than packed dirt.',
        ],
        [
          'South Bass Island / Kelleys Island shore walks',
          'Ferry from Port Clinton, Catawba, or Marblehead',
          'Easy island paths · Easy',
          'Treat the ferry as part of the adventure. Island state parks offer short walks, glacial grooves (Kelleys), and shoreline views—not strenuous hiking.',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'A simple first-month plan (from the Columbus suburbs)',
      orderedList: [
        'Week 1: Inniswood or Glacier Ridge paved loop (30–45 minutes). Practice packing water and turning around on time.',
        'Week 2: Highbanks Overlook Trail, then a picnic. Optional: add Big Meadows Path if the group still has energy.',
        'Week 3: Shale Hollow or Blendon Woods (45–75 minutes on dirt and boardwalk).',
        'Week 4 day trip: Ash Cave + Conkle’s Hollow Gorge Trail (paved Hocking Hills), or Beaver Marsh + Towpath if you prefer a northbound drive.',
        'After month 1: Cedar Falls or Old Man’s Cave upper section, then Cemetery Ridge at Clear Creek (see below) or Cuyahoga Valley’s Ledges when stairs, hills, and uneven rock feel normal.',
      ],
      callout:
        'Living near Polaris? Highbanks and Shale Hollow are your default weeknight trails. See our picnic guide for pairing a short hike with lunch, and the mountain-biking guide if you want dirt wheels instead of hiking shoes.',
    },
    {
      type: 'table',
      title: 'Next step — Cemetery Ridge at Clear Creek Metro Park',
      intro:
        'Cemetery Ridge is not a first hike. It is the closest serious climb southeast of Columbus (Rockbridge, on the way toward Hocking Hills): 2.5 miles, moderate to difficult, dirt and gravel. Save it until the easy metro-park walks above feel comfortable. For a friends-group day plan, parking pin, and pack list, see [How to Prepare for Cemetery Ridge](/blog/cemetery-ridge-clear-creek-hike-prep).',
      headers: ['Trail', 'Trailhead / address', 'Distance & grade', 'What to expect'],
      rows: [
        [
          'Cemetery Ridge Trail',
          'Creekside Meadows area, 185 Clear Creek Rd, Rockbridge, OH 43149',
          '2.5 mi · Moderate–difficult · dirt & gravel',
          'Starts with a steep zigzag ascent through the woods, peaking at about 700 feet. On the ridge it alternates between forest and meadows. Out-and-back unless you link Fern or Hemlock. Extra water; skip after heavy rain—this trail gets muddy.',
        ],
        [
          'Creekside Meadows Trail (same park, easier)',
          'Same Creekside Meadows parking',
          '1.7 mi · Easy · grass along Clear Creek',
          'Use this if you drive to Clear Creek but are not ready for the ridge climb—flat creek-and-meadow walking. Cemetery Ridge begins from the same picnic area.',
        ],
      ],
    },
    {
      type: 'imageGallery',
      title: 'Cemetery Ridge & Clear Creek photos',
      intro:
        'Clear Creek Metro Park, Rockbridge — about an hour southeast of Columbus, on the way to Hocking Hills. Photos: Wikimedia Commons.',
      images: [
        {
          url: PHOTOS.cemeteryRidge,
          alt: 'Cemetery Ridge Trail Head wooden sign at Clear Creek Metro Park',
          caption: 'Cemetery Ridge Trail Head sign — Creekside Meadows picnic area. Steep zigzag starts just beyond.',
          mapUrl: googleMaps(MAPS.clearCreek.query),
        },
        {
          url: PHOTOS.clearCreek,
          alt: 'Sandstone outcrop along the road at Clear Creek Metro Park',
          caption: 'Blackhand sandstone along Clear Creek Road — same park as Cemetery Ridge.',
          mapUrl: googleMaps(MAPS.clearCreek.query),
        },
      ],
    },
    {
      type: 'table',
      title: 'Trailhead locations, parking & maps',
      intro:
        'Use these addresses in Google Maps or Apple Maps. Embedded maps below pin the four trailheads most people drive to first. Confirm lot hours on the park site — most are dawn to dusk.',
      headers: ['Place', 'Address', 'Parking / notes', 'Map'],
      rows: [
        [
          'Highbanks Metro Park',
          '9466 Columbus Pike (US 23 N), Lewis Center, OH 43035',
          'Main lot by the nature center. Overlook Trail starts nearby. Free.',
          `[Google Maps](${googleMaps(MAPS.highbanks.query)})`,
        ],
        [
          'Shale Hollow Preserve',
          '7799 Africa Rd, Lewis Center, OH 43035',
          'Small Preservation Parks lot. Compact trail system; can fill on weekends.',
          `[Google Maps](${googleMaps(MAPS.shaleHollow.query)})`,
        ],
        [
          'Battelle Darby Creek (bison / nature center)',
          '1775 Darby Creek Dr, Galloway, OH 43119',
          'Nature-center lots for bison viewing and greenway. Free Metro Parks.',
          `[Google Maps](${googleMaps('Battelle Darby Creek Metro Park Nature Center, 1775 Darby Creek Dr, Galloway, OH 43119')})`,
        ],
        [
          'Ash Cave',
          'Ash Cave parking, State Route 56, South Bloomingville / Logan area',
          'Dedicated Ash Cave lot. Paved 0.5 mi to the cave. Separate from Old Man’s Cave parking.',
          `[Google Maps](${googleMaps(MAPS.ashCave.query)})`,
        ],
        [
          'Hocking Hills Visitor Center / Old Man’s Cave',
          '19852 State Route 664 S, Logan, OH 43138',
          'Largest lot; fills by mid-morning on summer weekends. Start Upper Falls from here.',
          `[Google Maps](${googleMaps(MAPS.hockingVisitor.query)})`,
        ],
        [
          'Conkle’s Hollow',
          'Conkle’s Hollow State Nature Preserve, Big Pine Rd, Rockbridge / Logan',
          'Separate preserve lot — not inside the main state-park gorge. Gorge trail is paved; skip the rim.',
          `[Google Maps](${googleMaps(MAPS.conklesHollow.query)})`,
        ],
        [
          'Brandywine Falls (CVNP)',
          '8176 Brandywine Rd, Sagamore Hills, OH 44067',
          'National park lot at the falls. Upper platform is a short walk; stairs to lower views.',
          `[Google Maps](${googleMaps(MAPS.brandywine.query)})`,
        ],
        [
          'Beaver Marsh / Ira Trailhead',
          '3801 Riverview Rd, Peninsula, OH 44264',
          'Ira Trailhead lot. Boardwalk is essentially flat.',
          `[Google Maps](${googleMaps('Ira Trailhead, 3801 Riverview Rd, Peninsula, OH 44264')})`,
        ],
        [
          'Cemetery Ridge / Creekside Meadows',
          '185 Clear Creek Rd, Rockbridge, OH 43149',
          'Park at Creekside Meadows picnic area. Cemetery Ridge Trail Head sign is at the picnic grounds. ~1 hr from Columbus via US 33.',
          `[Google Maps](${googleMaps(MAPS.clearCreek.query)})`,
        ],
      ],
    },
    {
      type: 'mapEmbed',
      title: 'Trailhead maps',
      intro:
        'OpenStreetMap pins for four first-drive trailheads. Tap “Open in Google Maps” for turn-by-turn directions from Powell, Dublin, Polaris, or Delaware.',
      maps: [
        {
          title: 'Highbanks Metro Park',
          address: '9466 Columbus Pike, Lewis Center, OH 43035 — Overlook Trail & nature center.',
          embedUrl: osmEmbed(MAPS.highbanks.lat, MAPS.highbanks.lon),
          directionsUrl: googleMaps(MAPS.highbanks.query),
        },
        {
          title: 'Ash Cave',
          address: 'Hocking Hills State Park — paved beginner gorge trail.',
          embedUrl: osmEmbed(MAPS.ashCave.lat, MAPS.ashCave.lon),
          directionsUrl: googleMaps(MAPS.ashCave.query),
        },
        {
          title: 'Cemetery Ridge (Clear Creek)',
          address: 'Creekside Meadows, 185 Clear Creek Rd, Rockbridge, OH 43149.',
          embedUrl: osmEmbed(MAPS.clearCreek.lat, MAPS.clearCreek.lon, 0.02),
          directionsUrl: googleMaps(MAPS.clearCreek.query),
        },
        {
          title: 'Brandywine Falls',
          address: '8176 Brandywine Rd, Sagamore Hills, OH 44067 — Cuyahoga Valley National Park.',
          embedUrl: osmEmbed(MAPS.brandywine.lat, MAPS.brandywine.lon),
          directionsUrl: googleMaps(MAPS.brandywine.query),
        },
      ],
    },
    {
      type: 'panel',
      title: 'Safety, seasons, and trail etiquette',
      list: [
        'Stay on marked trails. Gorge rims, wet algae on stone, and unmarked shortcuts cause most beginner mishaps.',
        'After rain, postpone dirt trails 24–48 hours when you can—metro-park clay and Hocking Hills stairs get slick, and muddy boots widen trails.',
        'Ticks are common in Ohio tall grass and woods from spring through fall. Stay on path, check clothes after, and use repellent on skin and fabric as labeled.',
        'Summer: hike before 10 a.m. when possible. Gorges stay cooler than prairies; still bring more water than you think.',
        'Winter: packed snow on Overlook Trail and Towpath can be beginner-friendly with traction devices; skip icy gorge stairs.',
        'Yield: downhill hikers often step aside for uphill; give horses extra space on shared bridle trails; mountain-bike loops at Alum Creek and Chestnut Ridge are not hiking trails.',
        'Leave no trace: pack out snacks. Ohio’s free parks stay that way when they stay clean.',
        'Tell someone your trailhead and turnaround time if you are hiking alone.',
      ],
    },
    {
      type: 'panel',
      title: 'When you are ready for more',
      paragraphs: [
        'If 2–3 miles of dirt feels easy and you are comfortable on stairs, look at Cemetery Ridge at Clear Creek Metro Park (2.5 miles, steep zigzag to about 700 feet, then forest and meadows), Old Man’s Cave full gorge, Mohican’s forest trails, John Bryan / Clifton Gorge longer routes, and Cuyahoga Valley’s Ledges loop. Those are intermediate—not where this guide starts.',
        'Want a nature day with younger kids rather than a “hike”? Use the Ohio summer nature family guide. Want bikes on dirt? Use the Central Ohio mountain-biking trail guide and stay off hiking-only paths.',
      ],
    },
  ],
  relatedPostsTitle: 'More Ohio outdoor guides',
  relatedPosts: [
    {
      slug: 'rocks-and-roots-alum-creek-morning-hike-recap',
      title: 'Rocks and Roots Recap: Alum Creek Morning Hike (7:00–9:30 a.m.)',
      blurb: 'a local friends-group morning on the moderate Alum Creek loop.',
    },
    {
      slug: 'alum-creek-best-hike-spots-beginner-moderate',
      title: 'Best Alum Creek Hike Spots (Beginner to Moderate Plan)',
      blurb: 'closest hiking to Powell, Polaris, and Delaware — four outings, easy to moderate.',
    },
    {
      slug: 'cemetery-ridge-clear-creek-hike-prep',
      title: 'How to Prepare for Cemetery Ridge at Clear Creek Metro Park',
      blurb: 'pack list, Creekside Meadows parking, and a friends-group day plan.',
    },
    {
      slug: 'ohio-summer-nature-kids-family-guide',
      title: 'Ohio Summer Nature Guide for Families (Ages 3–13)',
      blurb: 'waterfalls, beaches, bison, and kid-paced park days statewide.',
    },
    {
      slug: 'half-day-picnic-near-polaris-ohio-kids-guide',
      title: 'Nature Picnic Day Near Polaris, Ohio',
      blurb: 'pair a short hike with lunch within 20 minutes of Polaris.',
    },
    {
      slug: 'mountain-biking-trails-powell-dublin-polaris-delaware-ohio',
      title: 'Best Mountain Biking Trails Near Powell, Dublin, Polaris & Delaware',
      blurb: 'when you want dirt wheels instead of hiking shoes.',
    },
    {
      slug: 'columbus-family-adventure-kids-10-13-guide',
      title: 'Best Family Adventures in Columbus, Ohio for Kids Ages 10–13',
      blurb: 'zip lines, caves, and bigger day trips after the first hikes click.',
    },
  ],
  referenceLinks: [
    {
      label: 'Ohio Department of Natural Resources — find a state park',
      url: 'https://ohiodnr.gov/go-and-do/plan-a-visit/find-a-property',
    },
    {
      label: 'Columbus and Franklin County Metro Parks — parks & trails',
      url: 'https://www.metroparks.net/',
    },
    {
      label: 'Preservation Parks of Delaware County — Shale Hollow',
      url: 'https://www.preservationparks.com/park/shale-hollow-preserve/',
    },
    {
      label: 'Metro Parks — Highbanks',
      url: 'https://www.metroparks.net/parks-and-trails/highbanks/',
    },
    {
      label: 'Metro Parks — Clear Creek (Cemetery Ridge Trail)',
      url: 'https://www.metroparks.net/parks-and-trails/clear-creek/',
    },
    {
      label: 'Wikimedia Commons — Cemetery Ridge Trail Head photo',
      url: 'https://commons.wikimedia.org/wiki/File:Cemetery_Ridge_Trail_head_(29278691062).jpg',
    },
    {
      label: 'Hocking Hills State Park (ODNR)',
      url: 'https://ohiodnr.gov/go-and-do/plan-a-visit/find-a-property/hocking-hills-state-park',
    },
    {
      label: 'Cuyahoga Valley National Park — plan your visit',
      url: 'https://www.nps.gov/cuva/planyourvisit/index.htm',
    },
    {
      label: 'Ohio DNR — Conkle’s Hollow State Nature Preserve',
      url: 'https://ohiodnr.gov/go-and-do/plan-a-visit/find-a-property/conkles-hollow-state-nature-preserve',
    },
    {
      label: 'Maumee Bay State Park (ODNR)',
      url: 'https://ohiodnr.gov/go-and-do/plan-a-visit/find-a-property/maumee-bay-state-park',
    },
  ],
};
